import { CircularProgress } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { Rating } from "@mui/material";
import CustomStarIcon from "../../../components/CustomStarIcon";
import InputAmount from "../../../components/inputs/InputAmount";
import { InputComment } from "../../../components/inputs/InputComment";
import AzulPaymentForm from "../../../components/AzulPaymentForm";
import { LegalModal } from "../../../components/LegalModal";
import TipService from "../../../services/TipService";
import AzulPaymentService from "../../../services/AzulPaymentService";
import { GetEmployeeResponseDto } from "../../../services/dtos/response/UserResponseDto";
import QRLandingBase from "../QRLandingBase";
import MastercardLogoIdentityCheck from "../../../assets/img/mastercardidentitycheck.png";
import VisaSecureLogo from "../../../assets/img/visa-secure.png";
import VisaLogo from "../../../assets/img/visa-logo.jpg";
import MastercardLogo from "../../../assets/img/mastercard-logo.jpg";

const StyledRating = styled(Rating)(({ theme }) => ({
  "& .MuiRating-iconFilled": {
    color: "#f3c433",
  },
  "& .MuiRating-iconHover": {
    color: "#f3c433",
  },
}));

interface QRLandingDOProps {
  employee: GetEmployeeResponseDto;
  error: { [key: string]: string };
  rating: number | null;
  animatedRating: number;
  isAnimating: boolean;
  comment: string | null;
  setComment: (value: string | null) => void;
  setError: (error: { [key: string]: string }) => void;
  handleRatingChange: (event: React.ChangeEvent<{}>, newRating: number | null) => void;
  handleRatingChangeActive: (event: React.SyntheticEvent<Element, Event>, value: number | null) => void;
  MAX_CHARACTERS: number;
  userId: string | null;
  branchId: string | null;
}

const QRLandingDO = ({
  employee,
  error,
  rating,
  animatedRating,
  isAnimating,
  comment,
  setComment,
  setError,
  handleRatingChange,
  handleRatingChangeActive,
  MAX_CHARACTERS,
  userId,
  branchId,
}: QRLandingDOProps) => {
  const service = useMemo(() => new TipService(), []);
  const azulPaymentService = useMemo(() => new AzulPaymentService(), []);
  const navigate = useNavigate();
  const [amount, setAmount] = useState<string>("");
  const [isLoadingTip, setIsLoadingTip] = useState(false);
  const [isLoadingNoTip, setIsLoadingNoTip] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);

  const sendTip = useCallback(async () => {
    setIsLoadingTip(true);
    const numericAmount = Number(amount);

    // Validar que haya un monto válido
    if (!amount || isNaN(numericAmount) || numericAmount <= 0) {
      setError({
        payment: "Por favor, ingrese un monto válido",
      });
      setIsLoadingTip(false);
      return;
    }

    // Validar que haya una valoración si hay comentario
    if (comment && !rating) {
      setError({
        comment: "Por favor, ingrese una valoración",
      });
      setIsLoadingTip(false);
      return;
    }

    try {
      // Usar el endpoint /tips/pay para guardar rating y tip en una sola llamada
      service.pay(
        {
          Amount: numericAmount,
          EmployeeId: +userId!,
          BranchId: +branchId!,
          RatingMessage: comment,
          RatingValue: rating,
          TipOriginId: "AZUL",
          CountryCode: employee?.countryIsoCode?.toUpperCase() || "DO",
        },
        (response) => {
          // La respuesta puede ser directamente el ID (número o string) o un objeto con tip.tipId
          let tipId: number | string | null = null;
          
          if (typeof response === 'number' || typeof response === 'string') {
            tipId = typeof response === 'string' ? parseInt(response, 10) : response;
          } else if (response && typeof response === 'object') {
            tipId = response?.tip?.tipId || response?.tipId || null;
          }
          
          if (!tipId || tipId === 0) {
            setError({
              payment: "Error al obtener el ID de la propina. Por favor, intente nuevamente.",
            });
            setIsLoadingTip(false);
            return;
          }
          
          try {
            // Guardar tipId en localStorage y sessionStorage antes de redirigir a Azul
            if (tipId) {
              const tipIdString = String(tipId);
              sessionStorage.setItem('azulTipId', tipIdString);
            } else {
              console.error("[AZUL] ERROR: tipId es null o undefined, no se puede guardar");
            }
            
            // Guardar rating y googleLink para usar después del pago exitoso
            if (rating !== null) {
              sessionStorage.setItem('azulRating', String(rating));
            }
            if (employee?.googleLink) {
              sessionStorage.setItem('azulGoogleLink', employee.googleLink);
            }
            
            // Usar setTimeout para asegurar que el localStorage se guarde antes de la redirección
            setTimeout(() => {
              azulPaymentService.processPayment({
                amount: numericAmount,
                orderNumber: String(tipId),
                customOrderId: String(tipId),
                userId: userId,
                branchId: branchId,
              });
              setIsLoadingTip(false);
            }, 120);
          } catch (error: any) {
            setError({
              payment: error.message || "Error al procesar el pago. Por favor, intente nuevamente.",
            });
            setIsLoadingTip(false);
          }
        },
        (errorMessage) => {
          setError({ payment: errorMessage });
          setIsLoadingTip(false);
        }
      );
    } catch (error: any) {
      setError({
        payment: error.message || "Error al procesar el pago. Por favor, intente nuevamente.",
      });
      setIsLoadingTip(false);
    }
  }, [amount, comment, rating, setError, azulPaymentService, service, branchId, userId, employee]);

  const handleWithoutTipPayment = useCallback(async () => {
    setIsLoadingNoTip(true);

    if (!rating) {
      setIsLoadingNoTip(false);
      setError({
        comment: "Por favor, ingrese una valoración",
      });
      return;
    }

    await service.withoutTip(
      {
        BranchId: +branchId!,
        EmployeeId: +userId!,
        RatingMessage: comment,
        RatingValue: rating,
        CountryCode: employee?.countryIsoCode?.toUpperCase() || "DO",
      },
      () => {
        if(rating && rating >= 4 && employee?.googleLink && employee?.googleLink.length > 0){
          if(employee?.googleLink?.includes("https://")){
            window.location.replace(employee?.googleLink);
          } else {
            window.location.replace(`https://${employee?.googleLink}`);
          }
        } else {
          navigate("/confirmation", {
            state: {
              rating: rating,
              googleLink: employee?.googleLink,
              userId: userId,
              branchId: branchId,
            },
          });
        }
        setIsLoadingNoTip(false);
      },
      (errorMessage) => {
        setError({ payment: errorMessage });
        setIsLoadingNoTip(false);
      }
    );
  }, [rating, comment, branchId, userId, employee, navigate, service, setError]);

  return (
    <QRLandingBase employee={employee} error={error}>
      <LegalModal open={legalOpen} onClose={() => setLegalOpen(false)} />
      <div className="flex-1 flex flex-col w-full items-center min-h-0 py-1">
      <div className="flex flex-col items-center justify-center">
        <StyledRating
          name="simple-controlled"
          value={rating !== null ? rating : animatedRating}
          onChange={handleRatingChange}
          onChangeActive={handleRatingChangeActive}
          size="large"
          icon={<CustomStarIcon isAnimating={isAnimating} />}
          emptyIcon={<CustomStarIcon />}
          sx={{ fontSize: "2.25rem" }}
        />
        {error.comment && (
          <span className="text-red-500 text-sm">{error.comment}</span>
        )}
      </div>
      <div className="h-fit w-full max-w-xs lg:max-w-xl pt-2 px-4 text-[#424242] lg:px-16">
        <InputComment
          onChange={(value: string) => {
            if (value.length <= MAX_CHARACTERS) {
              setComment(value);
            }
          }}
          value={comment}
          maxCharacters={MAX_CHARACTERS}
        />
        <p className="text-xs lg:text-sm text-center pt-1 text-[#3a3a3a] font-medium">
          Los mensajes solo serán vistos por el manager del establecimiento
        </p>
      </div>
      <div className="max-w-xs text-[#424242] flex text-4xl md:text-5xl pt-1 items-center">
        <InputAmount amount={amount} setAmount={setAmount} currencySymbol="RD$" />
      </div>
      <div className="h-[1px] max-w-xs w-full shrink-0 bg-[#e0e0e0]" />

      {error.payment && (
        <span className="text-red-500 text-sm">{error.payment}</span>
      )}

      <div className="pt-4 flex justify-between gap-4 lg:max-w-lg px-4 items-center footer sm:px-10">
        <button
          onClick={handleWithoutTipPayment}
          disabled={isLoadingNoTip || isLoadingTip}
          className="rounded-full py-1 w-40 disabled:opacity-50"
          style={{
            backgroundColor: employee.headerLandingColor ?? "#3b3b3b",
            color: employee.textLandingColor ?? "#fff",
          }}
          translate="no"
        >
          {isLoadingNoTip ? (
            <div className="flex items-center justify-center">
              <CircularProgress
                sx={{
                  color: "#fff",
                }}
                size="1.5rem"
                className="animate-spin "
              />
            </div>
          ) : (
            <span>SIN PROPINA</span>
          )}
        </button>
        <button
          disabled={
            amount === null || +amount <= 0 || isLoadingTip || isLoadingNoTip
          }
          className="disabled:opacity-40 rounded-full py-1 w-40"
          style={{
            backgroundColor: employee.headerLandingColor ?? "#f20bbb",
            color: employee.textLandingColor ?? "#fff",
          }}
          onClick={sendTip}
          translate="no"
        >
          {isLoadingTip ? (
            <div className="flex items-center justify-center">
              <CircularProgress
                sx={{
                  color: "#fff",
                }}
                size="1.5rem"
                className="animate-spin "
              />
            </div>
          ) : (
            "DEJAR PROPINA"
          )}
        </button>
      </div>
      <AzulPaymentForm azulPaymentService={azulPaymentService} userId={userId} branchId={branchId} />
      <footer className="mt-auto w-full max-w-full pt-10 sm:pt-12 md:pt-14 pb-4 px-3 min-h-0 flex flex-col items-center justify-end gap-3">
        <div className="flex items-center justify-center gap-1.5 sm:gap-2 flex-wrap">
          <img
            src={MastercardLogoIdentityCheck}
            alt="Mastercard Identity Check"
            width={56}
            height={36}
            className="h-9 w-auto max-h-9 object-contain object-center"
          />
          <img
            src={VisaSecureLogo}
            alt="Visa Secure"
            width={56}
            height={36}
            className="h-9 w-auto max-h-9 object-contain object-center"
          />
          <img
            src={VisaLogo}
            alt="Visa"
            width={56}
            height={36}
            className="h-9 w-auto max-h-9 object-contain object-center"
          />
          <img
            src={MastercardLogo}
            alt="Mastercard"
            width={56}
            height={36}
            className="h-9 w-auto max-h-9 object-contain object-center"
          />
        </div>
        <div className="text-center w-full max-w-full">
          <p className="text-[10px] md:text-[10px] text-[#424242] pb-0.5 break-words">
            Servicio al Cliente: info@livom.net
          </p>
          <p className="text-[8px] md:text-[11px] text-[#424242]/80 break-words leading-tight">
            Divix SRL, CALLE FEDERICO GERALDINO NUM. 94, PARAISO, SANTO DOMINGO, DISTRITO NACIONAL, REPÚBLICA DOMINICANA.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setLegalOpen(true)}
          className="inline-flex bg-[#3b3b3b] text-white items-center justify-center whitespace-nowrap rounded-xl md:rounded-2xl text-xs md:text-sm font-medium transition-colors hover:bg-[#2d2d2d] px-3 py-1 md:px-5"
        >
          Legales
        </button>
      </footer>
      </div>
    </QRLandingBase>
  );
};

export default QRLandingDO;

